import { useEffect, useRef, useState } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Icon,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import {
  FaChevronDown,
  FaCloudDownloadAlt,
  FaFileInvoice,
  FaTicketAlt,
  FaClock,
  FaUsers,
  FaChevronUp,
  FaClipboard,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import actions from "src/actions";

type CustomerReportType =
  | "settlement-pdf"
  | "tickets"
  | "waitlist"
  | "sales-pdf"
  | "guestlist";

interface ReportOption {
  key: CustomerReportType;
  title: string;
  icon: any;
  description: string;
}

const reportOptions: ReportOption[] = [
  {
    key: "settlement-pdf",
    title: "Settlement Report",
    icon: FaFileInvoice,
    description: "All sales data (PDF)",
  },
  {
    key: "tickets",
    title: "Ticket Holders Report",
    icon: FaTicketAlt,
    description: "List of all ticket holders (CSV)",
  },
  {
    key: "waitlist",
    title: "Waitlist Report",
    icon: FaClock,
    description: "Users on the event waitlist (CSV)",
  },
  {
    key: "sales-pdf",
    title: "Sales Report",
    icon: FaUsers,
    description:
      "Sales data without refunds, chargebacks, or waitlist activity (PDF)",
  },
  {
    key: "guestlist",
    title: "Guestlist Report",
    icon: FaClipboard,
    description: "Names, notes, tags, tickets, and check-in time (CSV)",
  },
];

const POLL_INTERVAL_MS = 2000;
// generateEventReport may run for 900s. Keep polling past that so a finish
// or failure in the last minute is seen before another job can be started.
const REPORT_WORKER_TIMEOUT_MS = 900_000;
const POLL_GRACE_MS = 60_000;
const POLL_DEADLINE_MS = REPORT_WORKER_TIMEOUT_MS + POLL_GRACE_MS;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const DownloadReportsDropdown = () => {
  const { event_id } = useParams();
  const toast = useToast();
  const [pendingType, setPendingType] = useState<CustomerReportType | null>(
    null
  );
  const [errors, setErrors] = useState<
    Partial<Record<CustomerReportType, string>>
  >({});
  const cancelled = useRef(false);

  useEffect(() => {
    return () => {
      cancelled.current = true;
    };
  }, []);

  const handleDownload = async (reportType: CustomerReportType) => {
    if (!event_id || pendingType) {
      return;
    }

    setPendingType(reportType);
    setErrors((current) => ({ ...current, [reportType]: undefined }));

    try {
      const created = await actions.ADMIN.createEventReportJob({
        event_id,
        report_type: reportType,
      });

      const startedAt = Date.now();
      while (Date.now() - startedAt < POLL_DEADLINE_MS) {
        if (cancelled.current) {
          return;
        }
        const job = await actions.ADMIN.fetchEventReportJob({
          event_id,
          job_id: created.job_id,
        });
        if (job.status === "COMPLETED") {
          if (!job.url) {
            throw new Error("Report finished without a download URL");
          }
          window.location.assign(job.url);
          return;
        }
        if (job.status === "FAILED") {
          throw new Error("Report failed");
        }
        const remaining = POLL_DEADLINE_MS - (Date.now() - startedAt);
        if (remaining <= 0) {
          break;
        }
        await sleep(Math.min(POLL_INTERVAL_MS, remaining));
      }

      throw new Error("Report is still generating. Try the download again.");
    } catch (err: any) {
      const message =
        err?.message === "Report failed" ||
        err?.message === "Report finished without a download URL" ||
        err?.message === "Report is still generating. Try the download again."
          ? err.message
          : "Report failed";
      setErrors((current) => ({ ...current, [reportType]: message }));
      toast({
        status: "error",
        title: "Report failed",
        description: message,
      });
    } finally {
      if (!cancelled.current) {
        setPendingType(null);
      }
    }
  };

  return (
    <Menu closeOnSelect={false}>
      {({ isOpen }) => (
        <>
          <MenuButton
            as={Button}
            rightIcon={isOpen ? <FaChevronUp /> : <FaChevronDown />}
            leftIcon={<FaCloudDownloadAlt />}
            variant="outline"
            w={"280px"}
            isDisabled={!event_id}
            isLoading={!!pendingType}
            loadingText="Generating report"
          >
            Download Reports
          </MenuButton>
          <MenuList>
            {reportOptions.map((report) => {
              const isPending = pendingType === report.key;
              const error = errors[report.key];
              return (
                <MenuItem
                  key={report.key}
                  onClick={() => handleDownload(report.key)}
                  icon={<Icon as={report.icon} />}
                  isDisabled={!!pendingType}
                >
                  <VStack align="flex-start" spacing={0} w={["full", "300px"]}>
                    <Text fontWeight="medium">
                      {isPending ? `${report.title}…` : report.title}
                    </Text>
                    <Text
                      fontSize="xs"
                      color={error ? "red.600" : "gray.600"}
                    >
                      {isPending
                        ? "Generating…"
                        : error || report.description}
                    </Text>
                  </VStack>
                </MenuItem>
              );
            })}
          </MenuList>
        </>
      )}
    </Menu>
  );
};

export default DownloadReportsDropdown;
